import { ReactElement, useEffect, useMemo, useState } from 'react';
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Tab,
    Tabs,
    TextField,
    Typography
} from '@mui/material';

import {
    useUpdateSettingDescriptionMutation,
    useUpdateSettingOverridesMutation,
    useUpdateSettingValueMutation
} from '../../../services/rtk/SettingsAPI';
import { RawSetting, SettingOverride, ValueType } from '../../../types/UserSettings';
import OverrideEditor from './OverrideEditor';
import ValueInput from './ValueInput';

// Order-independent JSON equality: treats null, undefined, and missing properties
// as equivalent so server-normalized payloads round-trip cleanly.
const stableStringify = (v: unknown): string => {
    if (v === undefined || v === null) return 'null';
    if (typeof v !== 'object') return JSON.stringify(v);
    if (Array.isArray(v)) {
        const items = v.map(stableStringify).join(',');
        return `[${items}]`;
    }
    const obj = v as Record<string, unknown>;
    const keys = Object.keys(obj)
        .filter((k) => obj[k] !== undefined && obj[k] !== null)
        .sort((a, b) => a.localeCompare(b));
    const entries = keys.map((k) => `${JSON.stringify(k)}:${stableStringify(obj[k])}`).join(',');
    return `{${entries}}`;
};
const isStablyEqual = (a: unknown, b: unknown): boolean => stableStringify(a) === stableStringify(b);

interface ValueTabProps {
    description: string;
    setDescription: (v: string) => void;
    value: unknown;
    setValue: (v: unknown) => void;
    settingValue: unknown;
    reason: string;
    setReason: (v: string) => void;
    jsonError: string;
    setJsonError: (v: string) => void;
    valueType?: ValueType;
}

function ValueTab({
    description,
    setDescription,
    value,
    setValue,
    settingValue,
    reason,
    setReason,
    jsonError,
    setJsonError,
    valueType
}: Readonly<ValueTabProps>): ReactElement {
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
                label="Description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                size="small"
                fullWidth
                data-testid="description-input"
            />
            <Box>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                    Default Value
                </Typography>
                <ValueInput
                    value={value}
                    valueType={valueType}
                    onChange={(v) => {
                        setValue(v);
                        if (typeof settingValue === 'object' && settingValue !== null) {
                            try {
                                JSON.stringify(v);
                                setJsonError('');
                            } catch {
                                setJsonError('Invalid JSON');
                            }
                        }
                    }}
                    error={jsonError}
                />
            </Box>
            <TextField
                label="Reason for change"
                required
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                size="small"
                fullWidth
                data-testid="value-reason-input"
            />
        </Box>
    );
}

interface OverridesTabProps {
    overrides: SettingOverride[];
    setOverrides: (v: SettingOverride[]) => void;
    defaultValue: unknown;
    reason: string;
    setReason: (v: string) => void;
    valueType?: ValueType;
}

function OverridesTab({
    overrides,
    setOverrides,
    defaultValue,
    reason,
    setReason,
    valueType
}: Readonly<OverridesTabProps>): ReactElement {
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <OverrideEditor
                overrides={overrides}
                defaultValue={defaultValue}
                valueType={valueType}
                onChange={setOverrides}
            />
            <TextField
                label="Reason for change"
                required
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                size="small"
                fullWidth
                data-testid="overrides-reason-input"
            />
        </Box>
    );
}

interface Props {
    settingKey: string;
    setting: RawSetting;
    open: boolean;
    onClose: () => void;
}

function EditSettingDialog({ settingKey, setting, open, onClose }: Readonly<Props>): ReactElement {
    const [tab, setTab] = useState(0);
    const [description, setDescription] = useState(setting.description);
    const [value, setValue] = useState(setting.value);
    const [valueReason, setValueReason] = useState('');
    const [valueJsonError, setValueJsonError] = useState('');
    const [overrides, setOverrides] = useState<SettingOverride[]>(setting.overrides ?? []);
    const [overridesReason, setOverridesReason] = useState('');

    const [updateValue, { isLoading: savingValue }] = useUpdateSettingValueMutation();
    const [updateDescription, { isLoading: savingDescription }] = useUpdateSettingDescriptionMutation();
    const [updateOverrides, { isLoading: savingOverrides }] = useUpdateSettingOverridesMutation();

    // Reset when setting changes or dialog opens
    useEffect(() => {
        setDescription(setting.description);
        setValue(setting.value);
        setValueReason('');
        setValueJsonError('');
        setOverrides(setting.overrides ?? []);
        setOverridesReason('');
        setTab(0);
    }, [settingKey, open]);

    const descriptionDirty = description !== setting.description;
    const valueDirty = useMemo(() => !isStablyEqual(value, setting.value), [value, setting.value]);
    const tab1Dirty = descriptionDirty || valueDirty;
    const tab2Dirty = useMemo(() => !isStablyEqual(overrides, setting.overrides ?? []), [overrides, setting.overrides]);
    const isDirty = tab1Dirty || tab2Dirty;
    const isSaving = savingValue || savingDescription || savingOverrides;

    const canSave =
        isDirty &&
        !isSaving &&
        (!tab1Dirty || (valueReason.trim() !== '' && valueJsonError === '')) &&
        (!tab2Dirty || overridesReason.trim() !== '');

    const handleCloseWithGuard = () => {
        if (isDirty && !confirm('You have unsaved changes. Discard them?')) return;
        onClose();
    };

    // Sequential awaits so each response reflects all prior writes. Ordered
    // description -> value -> overrides; the overrides response (which echoes
    // the current default value) therefore sees the new value if both changed.
    const handleSave = async () => {
        if (tab1Dirty) {
            if (descriptionDirty) {
                await updateDescription({ key: settingKey, body: { reason: valueReason, description } }).unwrap();
            }
            if (valueDirty) {
                await updateValue({ key: settingKey, body: { reason: valueReason, value } }).unwrap();
            }
        }
        if (tab2Dirty) {
            await updateOverrides({ key: settingKey, body: { reason: overridesReason, overrides } }).unwrap();
        }
        setValueReason('');
        setOverridesReason('');
    };

    return (
        <Dialog open={open} onClose={handleCloseWithGuard} maxWidth="md" fullWidth data-testid="edit-setting-dialog">
            <DialogTitle>Edit: {settingKey}</DialogTitle>
            <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ px: 2, borderBottom: 1, borderColor: 'divider' }}>
                <Tab label={`Value & Description${tab1Dirty ? ' •' : ''}`} data-testid="tab-value" />
                <Tab label={`Overrides (${overrides.length})${tab2Dirty ? ' •' : ''}`} data-testid="tab-overrides" />
            </Tabs>

            <DialogContent sx={{ pt: 2 }}>
                {tab === 0 && (
                    <ValueTab
                        description={description}
                        setDescription={setDescription}
                        value={value}
                        setValue={setValue}
                        settingValue={setting.value}
                        reason={valueReason}
                        setReason={setValueReason}
                        jsonError={valueJsonError}
                        setJsonError={setValueJsonError}
                        valueType={setting.valueType}
                    />
                )}
                {tab === 1 && (
                    <OverridesTab
                        overrides={overrides}
                        setOverrides={setOverrides}
                        defaultValue={setting.value}
                        reason={overridesReason}
                        setReason={setOverridesReason}
                        valueType={setting.valueType}
                    />
                )}
            </DialogContent>

            <DialogActions>
                <Button onClick={handleCloseWithGuard}>Cancel</Button>
                <Button variant="contained" onClick={handleSave} disabled={!canSave} data-testid="save-btn">
                    {isSaving ? <CircularProgress size={20} /> : 'Save'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default EditSettingDialog;
